import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useLazyQuery } from '@apollo/client/react'
import { WatchQueryFetchPolicy } from '@apollo/client'
// components
import { MultiValueLabelProps, ReduxSelect } from 'components/FormElements/ReduxForm'
// graphql
import { DEFAULT_QUERY } from 'graphql/default'
// type
import { GroupBase } from 'react-select'
import { Validators } from 'utils/validators'
import { DocumentNode } from '@apollo/client/core'
import { WrappedFieldInputProps } from 'redux-form/lib/Field'
import { CustomSelectProps, IOption } from 'components/FormElements/Select'
// utils
import { debounce } from 'lodash'

interface Props extends CustomSelectProps, MultiValueLabelProps {
  skip?: boolean
  query: DocumentNode
  planId?: string
  validate?: Validators
  variables?: Record<string, any>
  getOptions: (data?: any) => any
  filterKeys?: string[][]
  fetchPolicy?: WatchQueryFetchPolicy
  handleSearch?: (data?: any) => any
  prepareOptions?: (options?: (IOption & any)[]) => any
  onSelectChange?: (value: IOption, input: WrappedFieldInputProps) => void
}

const PAGE_TAKE = 20

const getTotalCount = (data: Record<string, any>): number => {
  return Object.values(data).reduce((sum, data) => {
    const totalCount = data?.pagination?.totalCount || 0
    return sum + totalCount
  }, 0)
}

const ReduxAsyncPaginatedSelect: React.FC<Props> = ({
  skip,
  query,
  planId,
  variables,
  getOptions,
  filterKeys,
  fetchPolicy = 'network-only',
  handleSearch,
  prepareOptions,
  ...rest
}) => {
  const router = useRouter()

  const [hasMore, setHasMore] = React.useState(true)
  const [options, setOptions] = React.useState<IOption[] | GroupBase<IOption>[]>([])
  const [isSearching, setIsSearching] = React.useState(false)

  const pageRef = React.useRef(1)
  const cachedOptions = React.useRef<IOption[]>([])

  const [fetchOptions, { called, loading, refetch, fetchMore }] = useLazyQuery(query || DEFAULT_QUERY, {
    fetchPolicy
  })

  const getOptionsFromData = (data: any) => {
    return getOptions({ data, planId, router }) || []
  }

  const debouncedSearch = React.useCallback(
    debounce(async (search: string) => {
      if (!search) {
        setOptions(cachedOptions.current)
        setIsSearching(false)
        return
      }

      setIsSearching(true)
      const { data } = await refetch({
        // ...variables,
        ...(filterKeys && Object.fromEntries(filterKeys.map(([key, searchTerm]) => [key, { [searchTerm]: search }]))),
        pagination: {
          pageTake: 20,
          pageSkip: 0
        }
      })

      const newOptions = getOptionsFromData(data)

      setOptions(newOptions)
      setIsSearching(false)
    }, 500),
    []
  )

  const onSearch = (value: string) => {
    debouncedSearch(value)
  }

  const onMenuScrollToBottom = async () => {
    if (isSearching || !hasMore) {
      return
    }

    const { data } = await fetchMore({
      variables: {
        // ...variables,
        pagination: {
          pageTake: PAGE_TAKE,
          pageSkip: PAGE_TAKE * pageRef.current
        }
      }
    })

    const preparedOptions = getOptionsFromData(data)

    setOptions((prev) => {
      const updatedOptions = [...prev, ...preparedOptions]

      cachedOptions.current = updatedOptions

      const totalCount = getTotalCount(data)

      if (updatedOptions.length >= totalCount) {
        setHasMore(false)
      }

      return updatedOptions
    })

    pageRef.current += 1
  }

  const onMenuOpen = async () => {
    if (!called && query) {
      const { data } = await fetchOptions({
        variables: {
          // ...variables,
          pagination: {
            pageSkip: 0,
            pageTake: PAGE_TAKE
          }
        }
      })

      const preparedOptions = getOptionsFromData(data)

      setOptions(preparedOptions)
      cachedOptions.current = preparedOptions
    }
  }

  return (
    <ReduxSelect
      isLoading={loading}
      handleSearch={onSearch}
      onMenuOpen={onMenuOpen}
      onMenuScrollToBottom={onMenuScrollToBottom}
      options={prepareOptions ? prepareOptions(options) : options}
      {...rest}
    />
  )
}

export default ReduxAsyncPaginatedSelect
